import { z } from 'zod';
import { MigrationError, migrateProjectDocument } from '../domain/migrations';
import { IdSchema, assetRequiresBinary, formatIssues, normalizeMimeType, type Project } from '../domain/schemas';
import { IMAGE_HEADER_BYTES, MAX_IMAGE_BYTES, MAX_IMAGE_PIXELS, MAX_IMAGE_SIDE, readImageSize, sniffImage } from '../scene/imageFormats';
import { createId, nowIso } from '../lib/ids';
import type { StoredBlob } from './db';
import { StorageError } from './errors';
import { remapAssets, withoutAsset, type ProjectRepository } from './projectRepository';

export const EXPORT_FORMAT = 'sonicforge-project';
export const EXPORT_VERSION = 1;
export const MAX_IMPORT_BYTES = 50 * 1024 * 1024;

const EmbeddedBlobSchema = z.object({
  assetId: IdSchema,
  mimeType: z.string().max(100),
  base64: z.string().regex(/^[A-Za-z0-9+/]*={0,2}$/, 'Invalid base64 data'),
});

/** Outer envelope. The `project` payload is validated separately via migrations. */
const EnvelopeSchema = z.object({
  format: z.literal(EXPORT_FORMAT),
  exportVersion: z.number().int().min(1),
  exportedAt: z.string(),
  project: z.unknown(),
  blobs: z.array(EmbeddedBlobSchema).default([]),
});

export interface ExportFile {
  format: typeof EXPORT_FORMAT;
  exportVersion: number;
  exportedAt: string;
  project: Project;
  blobs: z.infer<typeof EmbeddedBlobSchema>[];
}

export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export function buildExport(project: Project, blobs: StoredBlob[] = []): ExportFile {
  return {
    format: EXPORT_FORMAT,
    exportVersion: EXPORT_VERSION,
    exportedAt: nowIso(),
    project,
    blobs: blobs.map((b) => ({ assetId: b.id, mimeType: b.mimeType, base64: arrayBufferToBase64(b.data) })),
  };
}

export function serializeExport(file: ExportFile): string {
  return JSON.stringify(file, null, 2);
}

export function exportFileName(project: Project): string {
  const slug = project.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'project';
  return `${slug}.sonicforge.json`;
}

/**
 * Cross-checks asset metadata against embedded binaries. Returns readable problems
 * (empty when consistent). Rules:
 *  - each binary appears once and belongs to an existing asset;
 *  - every asset that requires a binary (locally added files) has exactly one;
 *  - a binary's MIME type matches its asset's MIME type (case/parameters ignored);
 *  - a binary's byte length matches the asset's recorded size.
 */
export function checkAssetBinaries(project: Project, blobs: readonly StoredBlob[]): string[] {
  const problems: string[] = [];
  const assets = new Map(project.assets.map((a) => [a.id, a]));
  const seen = new Set<string>();
  for (const blob of blobs) {
    if (seen.has(blob.id)) {
      problems.push(`Embedded file "${blob.id}" appears more than once.`);
      continue;
    }
    seen.add(blob.id);
    const asset = assets.get(blob.id);
    if (!asset) {
      problems.push(`Embedded file "${blob.id}" does not belong to any project asset.`);
      continue;
    }
    if (normalizeMimeType(blob.mimeType) !== normalizeMimeType(asset.mimeType)) {
      problems.push(`Embedded file for "${asset.name}" is ${blob.mimeType || '(no type)'} but the asset says ${asset.mimeType}.`);
    }
    if (blob.data.byteLength !== asset.sizeBytes) {
      problems.push(`Embedded file for "${asset.name}" is ${blob.data.byteLength} bytes but the asset says ${asset.sizeBytes}.`);
    }
  }
  for (const asset of project.assets) {
    if (assetRequiresBinary(asset) && !seen.has(asset.id)) {
      problems.push(`The file data for "${asset.name}" is missing.`);
    }
  }
  return problems;
}

export interface ParsedImport {
  project: Project;
  blobs: StoredBlob[];
}

/**
 * Parses and fully validates an export file *without touching storage*.
 * Throws StorageError('invalid-file') with readable details on any problem.
 */
export function parseImport(text: string): ParsedImport {
  if (text.length > MAX_IMPORT_BYTES * 1.4) {
    throw new StorageError('invalid-file', 'The file is too large to import.');
  }
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    throw new StorageError('invalid-file', 'The file is not valid JSON.');
  }
  const envelope = EnvelopeSchema.safeParse(json);
  if (!envelope.success) {
    throw new StorageError('invalid-file', 'This is not a SonicForge project file.', formatIssues(envelope.error));
  }
  if (envelope.data.exportVersion > EXPORT_VERSION) {
    throw new StorageError(
      'invalid-file',
      `The file uses export format v${envelope.data.exportVersion}; this build supports up to v${EXPORT_VERSION}.`,
    );
  }
  let project: Project;
  try {
    project = migrateProjectDocument(envelope.data.project);
  } catch (error) {
    if (error instanceof MigrationError) throw new StorageError('invalid-file', error.message, error.details);
    throw error;
  }
  const blobs: StoredBlob[] = [];
  for (const blob of envelope.data.blobs) {
    let data: ArrayBuffer;
    try {
      data = base64ToArrayBuffer(blob.base64);
    } catch {
      throw new StorageError('invalid-file', `Embedded file "${blob.assetId}" is corrupted.`);
    }
    blobs.push({ id: blob.assetId, projectId: project.id, mimeType: blob.mimeType, data });
  }
  const problems = checkAssetBinaries(project, blobs);
  if (problems.length > 0) {
    throw new StorageError('invalid-file', 'The project file’s embedded files are inconsistent.', problems);
  }
  const unsafe = checkEmbeddedContent(project, blobs);
  if (unsafe.length > 0) {
    throw new StorageError('invalid-file', 'The project file contains files SonicForge cannot safely use.', unsafe);
  }
  return { project, blobs };
}

/**
 * Applies the checks of a direct image import to embedded images, whose metadata comes
 * from the same (possibly hand-made) file: they must really be PNG, JPEG or WebP within
 * the size and pixel limits, with the dimensions the asset declares (no decode bombs).
 */
export function checkEmbeddedContent(project: Project, blobs: readonly StoredBlob[]): string[] {
  const problems: string[] = [];
  const assets = new Map(project.assets.map((a) => [a.id, a]));
  for (const blob of blobs) {
    const asset = assets.get(blob.id);
    if (!asset) continue;
    const bytes = new Uint8Array(blob.data, 0, Math.min(blob.data.byteLength, IMAGE_HEADER_BYTES));
    if (asset.kind === 'image') {
      const format = sniffImage(bytes);
      const size = format ? readImageSize(format, bytes) : null;
      if (!format) problems.push(`“${asset.name}” is not a PNG, JPEG or WebP image.`);
      else if (blob.data.byteLength > MAX_IMAGE_BYTES) problems.push(`“${asset.name}” is larger than ${MAX_IMAGE_BYTES / 1048576} MB.`);
      else if (!size || size.width > MAX_IMAGE_SIDE || size.height > MAX_IMAGE_SIDE || size.width * size.height > MAX_IMAGE_PIXELS) {
        problems.push(`“${asset.name}” is ${size ? `${size.width} × ${size.height}` : 'of unknown size'}; images are limited to ${MAX_IMAGE_SIDE} × ${MAX_IMAGE_SIDE}.`);
      } else if (asset.image && (asset.image.width !== size.width || asset.image.height !== size.height)) {
        problems.push(`“${asset.name}” is ${size.width} × ${size.height} but the project says ${asset.image.width} × ${asset.image.height}.`);
      }
    }
    // Audio is not rejected here: Phase 0 accepted any upload, so legitimate old projects
    // can embed arbitrary bytes. Audio is only ever decoded by the browser's decoder, which
    // fails with a readable error (and the import itself is capped at 50 MB).
  }
  return problems;
}

/**
 * Imports a project file. Validation happens entirely before any write, and the write
 * itself is a single transaction, so an invalid or failing import can never corrupt or
 * partially overwrite existing projects. Imported projects always receive fresh asset
 * ids, and a fresh project id when the original id is already in use.
 */
export async function importProject(repo: ProjectRepository, text: string): Promise<Project> {
  const { project, blobs } = parseImport(text);
  const idMap = new Map(project.assets.map((a) => [a.id, createId('ast')]));
  const id = (await repo.exists(project.id)) ? createId('prj') : project.id;
  const now = nowIso();
  const prepared = remapAssets({ ...project, id, updatedAt: now }, idMap);
  const remappedBlobs = blobs.map((b) => ({ ...b, id: idMap.get(b.id) ?? b.id, projectId: id }));
  return repo.insert(prepared, remappedBlobs);
}

export interface OmittedFile {
  assetId: string;
  name: string;
  kind: string;
  /** missing: no longer stored; damaged: stored bytes unusable; unreadable: storage could not be read. */
  reason: 'missing' | 'damaged' | 'unreadable';
}

export interface UnsavedBackup {
  fileName: string;
  text: string;
  /** Files that could not be included; what used them was removed from the backup. */
  omitted: OmittedFile[];
  complete: boolean;
}

/**
 * An emergency backup of a project that is only in memory (its last save failed). The
 * result is always a file SonicForge can import:
 * - every stored file the project needs is embedded;
 * - a file that is no longer stored, or whose bytes would be rejected on import, is left
 *   out, together with everything that used it (`withoutAsset`, the same cleanup as
 *   deleting the file);
 * - the generated text is checked with the real import parser before it is returned.
 * `omitted` says exactly what is not in the file. The in-memory project is never modified.
 * Throws StorageError('validation') when no importable file can be produced.
 */
export async function exportUnsavedProject(repo: ProjectRepository, project: Project): Promise<UnsavedBackup> {
  let stored: StoredBlob[] = [];
  let unreadable = false;
  try {
    stored = await repo.blobsForProject(project.id);
  } catch {
    unreadable = true;
  }
  const byId = new Map(stored.map((b) => [b.id, b]));
  const omitted: OmittedFile[] = [];
  const kept: StoredBlob[] = [];
  for (const asset of project.assets) {
    if (!assetRequiresBinary(asset)) continue;
    const blob = byId.get(asset.id);
    const info = { assetId: asset.id, name: asset.name, kind: asset.kind };
    if (!blob) {
      omitted.push({ ...info, reason: unreadable ? 'unreadable' : 'missing' });
      continue;
    }
    const alone = { ...project, assets: [asset] };
    const damaged = checkAssetBinaries(alone, [blob]).length > 0 || checkEmbeddedContent(alone, [blob]).length > 0;
    if (damaged) omitted.push({ ...info, reason: 'damaged' });
    else kept.push(blob);
  }
  const backup = omitted.reduce((p, o) => withoutAsset(p, o.assetId), project);
  const text = serializeExport(buildExport(backup, kept));
  try {
    parseImport(text); // exactly what importing the file will check
  } catch (error) {
    const details = error instanceof StorageError ? [error.message, ...error.details] : [String(error)];
    throw new StorageError('validation', 'A backup that SonicForge can import could not be produced.', details, { cause: error });
  }
  return {
    fileName: exportFileName(project).replace(/\.sonicforge\.json$/, '-unsaved.sonicforge.json'),
    text,
    omitted,
    complete: omitted.length === 0,
  };
}

export async function exportProject(repo: ProjectRepository, projectId: string): Promise<{ fileName: string; text: string }> {
  const project = await repo.get(projectId);
  const blobs = await repo.blobsForProject(projectId);
  // Never produce a file that import would reject (e.g. a stored binary went missing).
  const problems = checkAssetBinaries(project, blobs);
  if (problems.length > 0) {
    throw new StorageError(
      'validation',
      'This project cannot be exported because some local files are damaged. Remove them in the Audio workspace and try again.',
      problems,
    );
  }
  return { fileName: exportFileName(project), text: serializeExport(buildExport(project, blobs)) };
}
